/**
 * tdesign v1.1.1
 * (c) 2023 tdesign
 * @license MIT
 */

import { _ as _slicedToArray } from '../_chunks/dep-fc63c737.mjs';
import { defineComponent, ref, onMounted, onUnmounted, computed, watch, createVNode, mergeProps } from 'vue';
import { o as omit_1 } from '../_chunks/dep-219a3220.mjs';
import { i as isFunction_1 } from '../_chunks/dep-8d19eb1a.mjs';
import { ImageIcon, ImageErrorIcon } from 'tdesign-icons-vue-next';
import observe from '../_common/js/utils/observe.mjs';
import { useConfig } from '../config-provider/useConfig.mjs';
import { useTNodeJSX, useTNodeDefault } from '../hooks/tnode.mjs';
import props from './props.mjs';
import { Space } from '../space/index.mjs';
import '../_chunks/dep-2a2a0e63.mjs';
import '../_chunks/dep-eba62589.mjs';
import '../_chunks/dep-94585de3.mjs';
import '../_chunks/dep-7d9e11c8.mjs';
import '../_chunks/dep-ae1abe1d.mjs';
import '../_chunks/dep-b9567f6a.mjs';
import '../_chunks/dep-654b4b01.mjs';
import '../_chunks/dep-06221fe2.mjs';
import '../_chunks/dep-a6022780.mjs';
import '../_chunks/dep-bd3d75f1.mjs';
import '../_chunks/dep-2b8d75e2.mjs';
import '../_chunks/dep-8f44163a.mjs';
import '../_chunks/dep-a255de71.mjs';
import '../_chunks/dep-ebe8bc2f.mjs';
import '../_chunks/dep-16e2f1a5.mjs';
import '../_chunks/dep-cc371884.mjs';
import '../_chunks/dep-1f0070bb.mjs';
import '../_chunks/dep-9d0d32fd.mjs';
import '../_chunks/dep-ebfafd52.mjs';
import '../_chunks/dep-a11a63ea.mjs';
import '../_chunks/dep-750294df.mjs';
import '../_chunks/dep-ad1accf9.mjs';
import '../_chunks/dep-e05e20e6.mjs';
import '../_chunks/dep-3e3494dc.mjs';
import '../_chunks/dep-42be3824.mjs';
import '../_chunks/dep-67dfe9c7.mjs';
import '../_chunks/dep-30c9b83d.mjs';
import '../_chunks/dep-0fa7026c.mjs';
import '../_chunks/dep-b32fb318.mjs';
import '../_chunks/dep-f7eab885.mjs';
import '../_chunks/dep-1041f7d7.mjs';
import '../_chunks/dep-adebe2ce.mjs';
import '../_chunks/dep-7f2f03b4.mjs';
import '../_chunks/dep-a0bd2e3a.mjs';
import '../_chunks/dep-91208ac3.mjs';
import '../_chunks/dep-9b57842b.mjs';
import '../_common/js/global-config/default-config.mjs';
import '../_common/js/global-config/locale/zh_CN.mjs';
import '../_chunks/dep-99a0abba.mjs';
import '../_chunks/dep-9d5ee0e1.mjs';
import '../config-provider/type.mjs';
import '../_chunks/dep-f50ac97c.mjs';
import '../_chunks/dep-d7053a2c.mjs';
import '../_chunks/dep-f3e3393e.mjs';
import '../_chunks/dep-31dc9d09.mjs';
import '../_chunks/dep-b9d74a58.mjs';
import '../utils/render-tnode.mjs';
import '../_chunks/dep-ff1c0573.mjs';
import '../space/space.mjs';
import '../_chunks/dep-b011b919.mjs';
import '../space/props.mjs';
import '../hooks/useConfig.mjs';
import '../hooks/slot.mjs';
import '../_chunks/dep-961339ce.mjs';
import '../utils/withInstall.mjs';
import './style/css.mjs';
import '../space/type.mjs';

var _Image = defineComponent({
  name: "TImage",
  props: props,
  setup: function setup(props2) {
    var imageRef = ref(null);
    var io = null;
    var renderTNodeJSX = useTNodeJSX();
    onMounted(function () {
      if (!props2.lazy || !imageRef.value) return;
      var ioObserver = observe(imageRef.value, null, handleLoadImage, 0);
      io = ioObserver;
    });
    onUnmounted(function () {
      imageRef.value && io && io.unobserve(imageRef.value);
    });
    var _useConfig = useConfig("image"),
      classPrefix = _useConfig.classPrefix,
      globalConfig = _useConfig.globalConfig;
    var imageSrc = computed(function () {
      return isFunction_1(globalConfig.value.replaceImageSrc) ? globalConfig.value.replaceImageSrc(props2) : props2.src;
    });
    watch(function () {
      return props2.src;
    }, function () {
      hasError.value = false;
      isLoaded.value = false;
    });
    var shouldLoad = ref(!props2.lazy);
    var handleLoadImage = function handleLoadImage() {
      shouldLoad.value = true;
    };
    var isLoaded = ref(false);
    var handleLoad = function handleLoad(e) {
      var _props2$onLoad;
      isLoaded.value = true;
      (_props2$onLoad = props2.onLoad) === null || _props2$onLoad === void 0 ? void 0 : _props2$onLoad.call(props2, {
        e: e
      });
    };
    var hasError = ref(false);
    var handleError = function handleError(e) {
      var _props2$onError;
      hasError.value = true;
      (_props2$onError = props2.onError) === null || _props2$onError === void 0 ? void 0 : _props2$onError.call(props2, {
        e: e
      });
    };
    var hasMouseEvent = computed(function () {
      return props2.overlayTrigger === "hover";
    });
    var imageClasses = computed(function () {
      return ["".concat(classPrefix.value, "-image"), "".concat(classPrefix.value, "-image--fit-").concat(props2.fit), "".concat(classPrefix.value, "-image--position-").concat(props2.position)];
    });
    var shouldShowOverlay = ref(!hasMouseEvent.value);
    var handleToggleOverlay = function handleToggleOverlay() {
      if (hasMouseEvent.value) {
        shouldShowOverlay.value = !shouldShowOverlay.value;
      }
    };
    var renderPlaceholder = function renderPlaceholder() {
      var placeholder = renderTNodeJSX("placeholder");
      if (!placeholder) return null;
      return createVNode("div", {
        "class": "".concat(classPrefix.value, "-image__placeholder")
      }, [placeholder]);
    };
    var renderGalleryShadow = function renderGalleryShadow() {
      if (!props2.gallery) return null;
      return createVNode("div", {
        "class": "".concat(classPrefix.value, "-image__gallery-shadow")
      }, null);
    };
    var renderOverlay = function renderOverlay() {
      var overlayContent = renderTNodDefault("overlayContent");
      if (!overlayContent) return null;
      return createVNode("div", {
        "class": ["".concat(classPrefix.value, "-image__overlay-content"), !shouldShowOverlay.value && "".concat(classPrefix.value, "-image__overlay-content--hidden")]
      }, [overlayContent]);
    };
    function renderImageSrcset() {
      return createVNode("picture", null, [Object.entries(props2.srcset).map(function (_ref) {
        var _ref2 = _slicedToArray(_ref, 2),
          type = _ref2[0],
          url = _ref2[1];
        return createVNode("source", {
          "type": type,
          "srcset": url
        }, null);
      }), props2.src && renderImage(props2.src)]);
    }
    function renderImage(url) {
      return createVNode("img", {
        "src": url,
        "onError": handleError,
        "onLoad": handleLoad,
        "class": imageClasses.value,
        "alt": props2.alt
      }, null);
    }
    var renderTNodDefault = useTNodeDefault();
    return function () {
      return createVNode("div", mergeProps({
        "ref": imageRef,
        "class": ["".concat(classPrefix.value, "-image__wrapper"), "".concat(classPrefix.value, "-image__wrapper--shape-").concat(props2.shape), props2.gallery && "".concat(classPrefix.value, "-image__wrapper--gallery"), hasMouseEvent.value && "".concat(classPrefix.value, "-image__wrapper--need-hover")],
        "onMouseenter": handleToggleOverlay,
        "onMouseleave": handleToggleOverlay
      }, omit_1(props2, ["src", "alt", "fit", "position", "shape", "placeholder", "loading", "error", "overlayTrigger", "overlayContent", "lazy", "gallery", "onLoad", "onError"])), [renderPlaceholder(), renderGalleryShadow(), (hasError.value || !shouldLoad.value) && createVNode("div", {
        "class": "".concat(classPrefix.value, "-image")
      }, null), !(hasError.value || !shouldLoad.value) && (props2.srcset && Object.keys(props2.srcset).length ? renderImageSrcset() : renderImage(imageSrc.value)), !(hasError.value || !shouldLoad.value) && !isLoaded.value && createVNode("div", {
        "class": "".concat(classPrefix.value, "-image__loading")
      }, [renderTNodeJSX("loading") || createVNode(Space, {
        "direction": "vertical",
        "size": 8,
        "align": "center"
      }, {
        "default": function _default() {
          return [createVNode(ImageIcon, {
            "size": "24px"
          }, null), globalConfig.value.loadingText];
        }
      })]), hasError.value && createVNode("div", {
        "class": "".concat(classPrefix.value, "-image__error")
      }, [renderTNodDefault("error", {
        defaultNode: createVNode(Space, {
          "direction": "vertical",
          "size": 8,
          "align": "center"
        }, {
          "default": function _default() {
            return [createVNode(ImageErrorIcon, {
              "size": "24px"
            }, null), globalConfig.value.errorText];
          }
        })
      })]), renderOverlay()]);
    };
  }
});

export { _Image as default };
//# sourceMappingURL=image.mjs.map
